// Contact activity log: every action taken on this conversation — each AI
// suggestion (intent, proposed actions, QA grade, model/PII meta) and the human
// decision recorded on it, newest first. Read-only; lives in the contact view.

import { useEffect, useRef, useState } from 'react'
import { flowLine } from './flowLine.js'
import {
  Box, Typography, Stack, Paper, Chip, Divider, CircularProgress, Alert, Tooltip, Button, Collapse, Link,
} from '@mui/material'
import { fetchSuggestions, fetchStepExecutions, fetchFeedbackForConversation } from './api.js'
// What each processed step submitted, and the Send Feedback reports with
// "View screen" (2026-10-29, "As the CSR saw it").
import { submittedSummary, submittedFieldRows } from './feedbackScreenState.js'
import FeedbackScreenView from './FeedbackScreenView.jsx'
import { ChevronIcon } from './icons.jsx'
import { looksLikeHtml, htmlToPlainText } from './richTextConversion.js'
import { FRUSTRATION_LABELS, teamLabel } from '@cxhub/shared'
import { parkActivityEntries, unparkReasonText, parkDurationMs, autoRequeueLine } from './parking.js'
import { TASKS_PATH } from './tasks.js'
import { vinEditActivityLine } from './vinEdit.js'
import { ViewCallsTrigger, ViewCallRow, useViewCallsModal } from './ViewCalls.jsx'
import { viewCallEntries } from './viewCalls.js'
import CallStatusChip from './CallStatusChip.jsx'
import { PendingStepChecklist } from './ReturnLabelPending.jsx'
import { useReturnLabels } from './useReturnLabels.js'

// An M365 rich-editor draft/sent-reply can be real HTML (e.g.
// "<p>...</p><p>[AGENT_FIRST_NAME]</p>"), which would otherwise show up as
// literal tags in this read-only log — every eBay suggestion's draft is
// already plain text and passes through unchanged.
const renderableText = (value) => (value && looksLikeHtml(value) ? htmlToPlainText(value) : value)

const QA_COLOR = { pass: 'success', revise: 'warning', block: 'error' }
const STATUS_COLOR = { approved: 'success', edited: 'success', escalated: 'warning', transferred: 'warning', dismissed: 'default', pending: 'info' }
// feature/frustration-capture — admin-only (this whole tab already is; see
// App.jsx's own isAdmin && view === 'activity' gate, and routes/ai.js's
// server-side strip for every non-admin role regardless). 0 calm .. 3 angry.
const FRUSTRATION_LEVEL_COLOR = { 0: 'default', 1: 'info', 2: 'warning', 3: 'error' }
const DIMENSIONS = [
  ['accuracyRelevance', 'Accuracy'],
  ['toneSentiment', 'Tone'],
  ['compliance', 'Compliance'],
  ['consistency', 'Consistency'],
]

function fmt(iso) {
  if (!iso) return ''
  try { return new Date(iso).toLocaleString() } catch { return iso }
}

// M:SS normally (e.g. "3:24" = 3 min 24 sec); H:MM:SS once handle time
// reaches an hour, so a long-running contact still reads unambiguously.
function formatHandleTime(ms) {
  if (typeof ms !== 'number') return null
  const totalSeconds = Math.max(0, Math.round(ms / 1000))
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60
  const pad = (n) => String(n).padStart(2, '0')
  return hours > 0 ? `${hours}:${pad(minutes)}:${pad(seconds)}` : `${minutes}:${pad(seconds)}`
}

function MetaLine({ meta }) {
  if (!meta) return null
  const models = [meta.draftModel, meta.gradeModel].filter(Boolean).join(' / ')
  const pii = meta.piiLanguage
  return (
    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
      {meta.provider && <Chip size="small" variant="outlined" label={`provider: ${meta.provider}`} />}
      {models && <Chip size="small" variant="outlined" label={`models: ${models}`} />}
      {typeof meta.redactionTotal === 'number' && <Chip size="small" variant="outlined" label={`redactions: ${meta.redactionTotal}`} />}
      {pii?.enabled && (
        <Chip size="small" variant="outlined" color={pii.error ? 'warning' : 'default'}
          label={pii.error ? `PII pass: ${pii.error}` : `PII masked: ${pii.masked ?? 0}`} />
      )}
      {typeof meta.elapsedMs === 'number' && <Chip size="small" variant="outlined" label={`${meta.elapsedMs}ms`} />}
    </Stack>
  )
}

const SERVICE_COLOR = { TRQ: 'primary', ItemInfo: 'secondary', 'eBay Post-Order': 'info', 'eBay Trading': 'info', 'eBay Fulfillment': 'info', AI: 'warning', 'Azure AI Language': 'warning' }
// Which button/action triggered this call — set on entries recorded after
// suggestion creation (Check/Process on a proposed step, or the action
// execution that runs automatically on Approve); absent on the calls made
// while the suggestion itself was being generated.
// invoice_preload (2026-10-29): the reply box's invoice preload's OMS reads.
const PHASE_LABEL = { check: 'Check', process: 'Process', approve: 'Approve', send: 'Send', invoice_preload: 'Invoice preload' }
// call.source: 'manual_panel' — set only for calls triggered from the manual
// actions sidebar (ManualActionsSidebar.jsx), never for the same phase/route
// triggered via SuggestionPanel.jsx's own Check/Process. Absent (undefined)
// for every other call, same as call.phase.
// persisted_order (2026-10-12): an email turn that named no order used the
// one an earlier message in the conversation resolved (linked_erp_order_id).
const SOURCE_LABEL = { manual_panel: 'Manual Panel', persisted_order: 'Order from an earlier message in this conversation' }

// Which template a draft, a step or a sent reply came from (docs/decisions.md,
// 2026-10-01) — the shared TemplateUsed shape { folder, title, source }.
const TEMPLATE_SOURCE_LABEL = { ai_selected: 'AI selected', fixed: 'Fixed template', agent_inserted: 'Inserted by agent' }

export function templateLineText(template) {
  if (!template?.title) return null
  return `Template: ${template.folder ? `${template.folder} / ` : ''}${template.title}`
}

// The AI draft's template: modelMeta.draftTemplate, or for an older
// suggestion the drafter's pick by title alone (selectedTemplateTitle).
export function draftTemplateOf(modelMeta) {
  if (modelMeta?.draftTemplate?.title) return modelMeta.draftTemplate
  if (modelMeta?.selectedTemplateTitle) return { folder: null, title: modelMeta.selectedTemplateTitle, source: 'ai_selected' }
  return null
}

// Renders nothing when no template was used — never a guess.
function TemplateLine({ template, sx }) {
  const text = templateLineText(template)
  if (!text) return null
  return (
    <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap sx={sx} data-testid="template-line">
      <Typography variant="caption" color="text.secondary">{text}</Typography>
      {TEMPLATE_SOURCE_LABEL[template.source] && <Chip size="small" variant="outlined" label={TEMPLATE_SOURCE_LABEL[template.source]} />}
    </Stack>
  )
}
// call.actedBy: the CSR who triggered a manual Process/Check call (routes/ai.js
// resolves it from req.auth's name/username/sub, same fallback chain as a
// decision's decidedBy) — set on every entry appended via
// appendSuggestionApiCalls() from the manual action routes, regardless of
// whether that specific action type also marks the suggestion decided.
// Absent (undefined) for calls made during suggestion generation or an
// unattended Flows-automated run.

// A call with something to show in the View call(s) modal.
const hasCallDetail = (call) => Boolean(call.endpoint || call.headers || call.requestBody || call.response || call.errorBody)

// The call's label in the View call(s) modal: its service and what it
// called (the label, or else the endpoint).
const activityCallLabel = (call) => [call.service, call.label ?? call.endpoint].filter(Boolean).join(': ') || null

function ApiCallEntry({ call }) {
  return (
    <Paper variant="outlined" sx={{ px: 1.5, py: 0.75, bgcolor: 'background.default', minWidth: 0 }}>
      <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap sx={{ minWidth: 0 }}>
        <Chip size="small" label={call.service} color={SERVICE_COLOR[call.service] ?? 'default'} variant="outlined" />
        {call.phase && <Chip size="small" variant="outlined" color="secondary" label={PHASE_LABEL[call.phase] ?? call.phase} />}
        {call.source && <Chip size="small" variant="outlined" label={SOURCE_LABEL[call.source] ?? call.source} />}
        {call.actedBy && <Chip size="small" variant="outlined" label={`by ${call.actedBy}`} />}
        {call.method && <Chip size="small" variant="outlined" label={call.method} sx={{ fontFamily: 'monospace' }} />}
        <Typography variant="caption" sx={{ fontFamily: 'monospace', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {call.endpoint ?? call.label ?? ''}
        </Typography>
        {typeof call.durationMs === 'number' && (
          <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>{call.durationMs}ms</Typography>
        )}
        {/* found / not found / error, the same chip as the View calls modal row (2026-10-29). */}
        <CallStatusChip call={call} />
      </Stack>
      {call.endpoint && call.label && (
        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', display: 'block', mt: 0.25, pl: 0.5, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
          {call.label}
        </Typography>
      )}
    </Paper>
  )
}

// View call(s) (2026-10-27): one trigger per entry's API calls, the shared
// modal (ViewCalls.jsx) in place of a "View Call" button per row.
// Each row keeps its chips and its error chip with the API's own message. The Activity
// Log is admin only (App.jsx), so the trigger is too, as the buttons were.
// Since 2026-10-29 the trigger is a small outlined "View N API calls"
// button, and each row with detail opens the modal at that call (click,
// Enter or Space); a row with nothing recorded (CHECKPOINT / NOTE) does not.
function ApiCallsSection({ apiCalls }) {
  const calls = apiCalls ?? []
  const viewable = []
  const viewIndex = calls.map((call) => {
    if (!hasCallDetail(call)) return null
    viewable.push({ label: activityCallLabel(call), call, logged: true })
    return viewable.length - 1
  })
  const list = viewCallEntries(viewable)
  const { openAt, modal } = useViewCallsModal(list)
  if (!calls.length) return null
  return (
    <Box sx={{ mt: 1.5 }}>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <Typography variant="caption" color="text.secondary" fontWeight={600}>
          API calls ({calls.length})
        </Typography>
        {list.length > 0 && <ViewCallsTrigger variant="button" count={list.length} testId="activity-view-call" onClick={() => openAt()} />}
      </Stack>
      <Stack spacing={0.5} sx={{ mt: 0.5 }}>
        {calls.map((call, i) => (viewIndex[i] === null
          ? <ApiCallEntry key={i} call={call} />
          : (
            <ViewCallRow key={i} ariaLabel={`API call ${i + 1} details`} onOpen={() => openAt(viewIndex[i])}>
              <ApiCallEntry call={call} />
            </ViewCallRow>
          )))}
      </Stack>
      {modal}
    </Box>
  )
}

// Distinct from ApiCallsSection above — a manual reclassification (the admin
// intent-override control, server/src/routes/ai.js's /intent-override route)
// is an admin action against this app's own data, not an external API call.
function ManualChangesSection({ overrides }) {
  if (!overrides?.length) return null
  return (
    <Box sx={{ mt: 1.5 }}>
      <Typography variant="caption" color="warning.main" fontWeight={600}>
        Manual Changes ({overrides.length})
      </Typography>
      <Stack spacing={0.5} sx={{ mt: 0.5 }}>
        {overrides.map((o, i) => (
          <Paper key={i} variant="outlined" sx={{ p: 1, borderColor: 'warning.main' }}>
            <Typography variant="caption" display="block">
              {o.type === 'intent_override'
                ? `Intent manually overridden to "${o.intent}"${o.setBy ? ` by ${o.setBy}` : ''}${o.setAt ? ` on ${fmt(o.setAt)}` : ''}`
                : o.type === 'team_override'
                  ? `Team manually overridden to "${teamLabel(o.team)}"${o.setBy ? ` by ${o.setBy}` : ''}${o.setAt ? ` on ${fmt(o.setAt)}` : ''}`
                  : JSON.stringify(o)}
            </Typography>
          </Paper>
        ))}
      </Stack>
    </Box>
  )
}

// One processed step's row (2026-10-29): when it ran, who ran it, and a
// one-line summary of what it submitted (action_step_executions.
// submitted_params), with an expand to every field. A step recorded before
// that column existed has none and gets no row: nothing extra, never "none".
function StepExecutionRow({ execution, label }) {
  const [open, setOpen] = useState(false)
  const summary = submittedSummary(execution.submittedParams)
  if (!summary) return null
  const rows = open ? submittedFieldRows(execution.submittedParams) : []
  return (
    <Paper variant="outlined" sx={{ px: 1.5, py: 0.75, bgcolor: 'background.default', minWidth: 0 }} data-testid="activity-step-execution">
      <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap sx={{ minWidth: 0 }}>
        <Chip size="small" color={execution.success ? 'success' : 'error'} variant="outlined" label={execution.success ? 'processed' : 'failed'} />
        {execution.source === 'automation' && <Chip size="small" variant="outlined" label="automation" />}
        {label && <Typography variant="caption" color="text.secondary">{label}</Typography>}
        <Typography variant="body2" data-testid="activity-step-summary" sx={{ flex: 1, minWidth: 0, wordBreak: 'break-word' }}>{summary}</Typography>
        <Typography variant="caption" color="text.secondary">{fmt(execution.executedAt)}{execution.actor && execution.source !== 'automation' ? ` · ${execution.actor}` : ''}</Typography>
        <Button size="small" onClick={() => setOpen((o) => !o)} aria-expanded={open} data-testid="activity-step-expand" sx={{ textTransform: 'none', minWidth: 0 }}>
          {open ? 'Hide fields' : 'All fields'}
        </Button>
      </Stack>
      {open && (
        <Box component="dl" data-testid="activity-step-fields" sx={{ m: 0, mt: 0.75, display: 'grid', gridTemplateColumns: 'minmax(120px, max-content) 1fr', columnGap: 1.5, rowGap: 0.25 }}>
          {rows.map((r, i) => (
            <Box key={i} sx={{ display: 'contents' }}>
              <Typography component="dt" variant="caption" color="text.secondary">{r.key}</Typography>
              <Typography component="dd" variant="caption" sx={{ m: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'monospace' }}>{r.value}</Typography>
            </Box>
          ))}
        </Box>
      )}
    </Paper>
  )
}

function StepExecutionsSection({ executions, actions }) {
  const shown = (executions ?? []).filter((e) => submittedSummary(e.submittedParams))
  if (!shown.length) return null
  return (
    <Box sx={{ mt: 1.5 }} data-testid="activity-step-executions">
      <Typography variant="caption" color="text.secondary" fontWeight={600}>Processed steps ({shown.length})</Typography>
      <Stack spacing={0.5} sx={{ mt: 0.5 }}>
        {shown.map((e) => <StepExecutionRow key={e.id} execution={e} label={actions?.[e.stepIndex]?.label ?? null} />)}
      </Stack>
    </Box>
  )
}

// A Send Feedback report in the history (2026-10-29): who sent it and when,
// the text, and "View screen" for an admin when it carries a screen state.
// Opened from the report (?feedback=<id>), it is marked "Reported here" and
// scrolled into view, so what happened just before and after is in sight.
function FeedbackEntry({ report, highlighted, isAdmin, onViewScreen }) {
  const ref = useRef(null)
  useEffect(() => {
    if (highlighted) ref.current?.scrollIntoView?.({ block: 'center' })
  }, [highlighted])
  return (
    <Paper ref={ref} variant="outlined" data-testid="activity-feedback" data-highlighted={highlighted ? '1' : undefined}
      sx={{ p: 1.5, ...(highlighted ? { borderColor: 'warning.main', borderWidth: 2 } : {}) }}>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        {highlighted && <Chip size="small" color="warning" label="Reported here" data-testid="activity-reported-here" />}
        <Typography variant="body2" fontWeight={600}>Send Feedback report by {report.submittedBy ?? 'someone'}</Typography>
        <Typography variant="caption" color="text.secondary">{fmt(report.createdAt)}</Typography>
        <Box sx={{ flexGrow: 1 }} />
        {isAdmin && report.hasScreenState && (
          <Link component="button" type="button" underline="hover" onClick={() => onViewScreen(report.id)} data-testid="activity-feedback-view-screen" sx={{ fontSize: 13 }}>
            View screen
          </Link>
        )}
      </Stack>
      {report.message && <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>{report.message}</Typography>}
    </Paper>
  )
}

function SuggestionCard({ s, expanded = true, onToggle, executions = null }) {
  const qa = s.qaGrade
  const decided = s.status !== 'pending'
  const bodyId = `activity-entry-body-${s.id}`
  const toggle = () => onToggle?.(s.id)
  return (
    <Paper variant="outlined" sx={{ p: 2 }} data-testid="activity-entry">
      {/* Header: the generated time, status, intent and who decided — click
          (or Enter/Space) to collapse or expand everything else below. */}
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        flexWrap="wrap"
        useFlexGap
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        aria-controls={bodyId}
        data-testid="activity-entry-header"
        onClick={toggle}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle() } }}
        sx={{ cursor: 'pointer', userSelect: 'none', borderRadius: 1, '&:focus-visible': { outline: 2, outlineColor: 'primary.main', outlineOffset: 2 } }}
      >
        <ChevronIcon expanded={expanded} fontSize="small" sx={{ color: 'text.secondary' }} />
        <Typography variant="caption" color="text.secondary">generated {fmt(s.createdAt)}</Typography>
        <Chip size="small" color={STATUS_COLOR[s.status] ?? 'default'} label={s.status} />
        <Chip size="small" label={`intent: ${s.intent}`} />
        {s.decidedBy && <Typography variant="caption" color="text.secondary">by {s.decidedBy}</Typography>}
      </Stack>

      <Collapse in={expanded} id={bodyId} data-testid="activity-entry-body">
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 1, mb: 1 }}>
        <Chip size="small" variant="outlined" label={`${Math.round((s.intentConfidence ?? 0) * 100)}% conf`} />
        {(() => {
          // Which flow, or why none (2026-10-14, ui/src/flowLine.js).
          const line = flowLine(s)
          if (!line) return null
          return <Chip size="small" variant="outlined" color={line.matched ? 'default' : 'warning'} label={line.text} data-testid="activity-flow-line" />
        })()}
        {qa && <Chip size="small" color={QA_COLOR[qa.overall?.recommendation] ?? 'default'} label={`QA: ${qa.overall?.recommendation} (${qa.overall?.score}/5)`} />}
        {(() => {
          // Both null (classifier ran, block missing/invalid) and
          // undefined (an older suggestion from before this feature
          // shipped — no key at all) render the same "not scored" chip;
          // only a real, validated object shows the scored chips.
          const f = s.modelMeta?.frustration
          if (!f) return <Chip size="small" variant="outlined" label="frustration: not scored" />
          return (
            <>
              <Tooltip title={f.evidence ? `"${f.evidence}"` : 'No evidence quoted (level 0)'}>
                <Chip size="small" color={FRUSTRATION_LEVEL_COLOR[f.level] ?? 'default'} label={`frustration: ${f.label} (${f.level})`} />
              </Tooltip>
              {f.peak && f.peak.level !== f.level && (
                <Chip
                  size="small"
                  variant="outlined"
                  color={FRUSTRATION_LEVEL_COLOR[f.peak.level] ?? 'default'}
                  label={`peak: ${FRUSTRATION_LABELS[f.peak.level] ?? f.peak.level}`}
                />
              )}
              {f.flags?.map((flag) => (
                <Chip key={flag} size="small" variant="outlined" color="error" label={flag.replaceAll('_', ' ')} />
              ))}
            </>
          )
        })()}
      </Stack>

      {s.intentReasoning && (
        <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1 }}>{s.intentReasoning}</Typography>
      )}
      {s.modelMeta?.frustration?.evidence && (
        <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1, fontStyle: 'italic' }}>
          frustration evidence: "{s.modelMeta.frustration.evidence}"
        </Typography>
      )}

      <MetaLine meta={s.modelMeta} />
      <ManualChangesSection overrides={s.modelMeta?.overrides} />
      <ApiCallsSection apiCalls={s.modelMeta?.apiCalls} />
      <StepExecutionsSection executions={executions} actions={s.proposedActions} />

      {s.proposedActions?.length > 0 && (
        <Box sx={{ mt: 1.5 }}>
          <Typography variant="caption" color="text.secondary" fontWeight={600}>
            Proposed steps (require approval — not auto-executed):
          </Typography>
          <Stack spacing={0.5} sx={{ mt: 0.75 }}>
            {s.proposedActions.map((a, i) => (
              <Paper key={i} variant="outlined" sx={{ px: 1.5, py: 0.75, bgcolor: 'background.default', display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                <Box sx={{
                  minWidth: 22, height: 22, borderRadius: '50%',
                  bgcolor: 'primary.main', color: 'primary.contrastText',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '0.7rem', fontWeight: 700, flexShrink: 0, mt: 0.1,
                }}>
                  {i + 1}
                </Box>
                <Stack spacing={0.25} sx={{ flex: 1 }}>
                  <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                    {a.targetSystem && <Chip size="small" color="primary" variant="outlined" label={a.targetSystem} />}
                    {a.type && <Chip size="small" variant="outlined" label={a.type} />}
                    {a.source && <Chip size="small" variant="outlined" label={SOURCE_LABEL[a.source] ?? a.source} />}
                    <Typography variant="body2">{a.label}</Typography>
                  </Stack>
                  <TemplateLine template={a.params?.template} sx={{ mt: 0.25 }} />
                  {a.params && Object.keys(a.params).some((k) => k !== 'template') && (
                    <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.25, fontFamily: 'monospace' }}>
                      {JSON.stringify(Object.fromEntries(Object.entries(a.params).filter(([k]) => k !== 'template')))}
                    </Typography>
                  )}
                  {a.rationale && (
                    <Typography variant="caption" color="text.secondary" component="div">{a.rationale}</Typography>
                  )}
                </Stack>
              </Paper>
            ))}
          </Stack>
        </Box>
      )}

      {qa && (
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
          {DIMENSIONS.map(([key, label]) => (
            <Tooltip key={key} title={qa[key]?.notes ?? ''}>
              <Chip size="small" variant="outlined" label={`${label}: ${qa[key]?.score ?? '–'}/5`} />
            </Tooltip>
          ))}
        </Stack>
      )}

      <Divider sx={{ my: 1.5 }} />

      <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
        <Typography variant="caption" color="text.secondary">AI draft</Typography>
        <TemplateLine template={draftTemplateOf(s.modelMeta)} />
      </Stack>
      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mb: 1 }}>{renderableText(s.draftReply) || '—'}</Typography>

      {decided ? (
        <Alert severity={STATUS_COLOR[s.status] === 'success' ? 'success' : 'info'} sx={{ '& .MuiAlert-message': { width: '100%' } }}>
          <Typography variant="body2" fontWeight={600}>
            {s.status}{s.decidedBy ? ` by ${s.decidedBy}` : ''}{s.decidedAt ? ` · ${fmt(s.decidedAt)}` : ''}
          </Typography>
          {/* escalatedBy/escalatedAt (migration 0047) survive a later decision
              on this same row, unlike decidedBy/decidedAt above — only shown
              once the row has moved PAST escalated (otherwise it's the same
              fact as the line above, just repeated). */}
          {/* Transfer to Support (2026-10-08): kept as history whatever
              happened after it. */}
          {s.transferredBy && (
            <Typography variant="caption" color="text.secondary" display="block" data-testid="activity-transfer">
              Transferred to Support by {s.transferredBy}{s.transferredAt ? ` · ${fmt(s.transferredAt)}` : ''}{s.transferNote ? `: ${s.transferNote}` : ''}
            </Typography>
          )}
          {s.escalatedBy && s.status !== 'escalated' && (
            <Typography variant="caption" color="text.secondary" display="block">
              Originally escalated by {s.escalatedBy}{s.escalatedAt ? ` · ${fmt(s.escalatedAt)}` : ''}
            </Typography>
          )}
          {s.humanReply && <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>{renderableText(s.humanReply)}</Typography>}
          {s.sentReply && (
            <Box sx={{ mt: 1, pt: 1, borderTop: 1, borderColor: 'divider' }}>
              <Typography variant="caption" fontWeight={600} display="block">
                Exact text sent to the customer
              </Typography>
              <TemplateLine template={s.sentTemplate} sx={{ mt: 0.25 }} />
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.25 }}>{renderableText(s.sentReply)}</Typography>
            </Box>
          )}
        </Alert>
      ) : (
        <Chip size="small" color="info" label="awaiting human decision" />
      )}
      </Collapse>
    </Paper>
  )
}

// One park or unpark (2026-10-02, Lab-only behind PARKING_ENABLED), placed in
// time order among the suggestion entries. A park says who, when and the
// note; an unpark says who, when, why and how long it was parked.
// A deferred order-lookup attempt (2026-10-14, prod contact 7288740): the
// turn found no order in TRQ yet and was retried later, so no suggestion
// exists for it. Its API calls are kept on the conversation
// (metadata.orderLookupAttempts) and listed here in time order.
function OrderLookupAttemptEntry({ attempt }) {
  return (
    <Paper variant="outlined" sx={{ p: 2 }} data-testid="activity-order-lookup-attempt">
      <Typography variant="caption" color="text.secondary" display="block">{fmt(attempt.at)}</Typography>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        Order lookup attempt {attempt.attempt} of {attempt.maxAttempts}: not found; retrying at {fmt(attempt.dueAt)}
      </Typography>
      <ApiCallsSection apiCalls={attempt.apiCalls ?? []} />
    </Paper>
  )
}

function ParkEntry({ entry }) {
  const p = entry.park
  return (
    <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'action.hover' }} data-testid={`activity-${entry.kind}`}>
      {entry.kind === 'park' ? (
        <>
          <Typography variant="body2" fontWeight={600}>Parked by {p.agentDisplayName ?? p.agent} · {fmt(p.parkedAt)}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>{p.note?.trim() ? p.note : 'no note'}</Typography>
        </>
      ) : (
        p.unparkReason === 'auto_requeued' ? (
          <Typography variant="body2" fontWeight={600}>{autoRequeueLine(p, fmt)}</Typography>
        ) : (
          <Typography variant="body2" fontWeight={600}>
            Unparked · {fmt(p.unparkedAt)} · {unparkReasonText(p)}{p.unparkReason === 'resumed' && p.unparkedBy ? ` by ${p.unparkedBy}` : ''} · parked for {formatHandleTime(parkDurationMs(p))}
          </Typography>
        )
      )}
    </Paper>
  )
}

// `refreshKey` lets the parent force a reload after a new decision/suggestion.
// `handleTimeMs`: elapsed ms from the agent's most recent assignment claim to
// the moment this conversation closed (repositories/conversations.js) — null
// until closed, or if no claim was ever recorded (e.g. a fully-automated
// close). Admin-only by construction: this component only ever
// renders inside the Activity Log tab, which App.jsx already gates off for
// plain csr agents — never displayed anywhere in the regular Agent view.
//
// This is ELAPSED SCOPE, deliberately: it ignores released_at, so a contact
// opened at 10:00 and closed at 13:00 reads as three hours even if the agent
// only held it for five minutes. That full span is the useful thing to see on
// ONE conversation. Reporting's Handle time deliberately measures something
// narrower (the clipped claim span, the only version that survives being
// averaged), so the two WILL disagree on the same contact — expected, not a
// bug (owner decision, 2026-08-07; see docs/decisions.md). If this number
// ever starts being read as a performance measure, relabel it ("Open for")
// rather than changing what it measures.
//
// PARKED time (2026-10-02, Lab-only) is the one thing both measures now leave
// out: handle time here excludes every parked interval inside its span, and
// parkedTimeMs shows the parked total on its own line beside it. `parks`
// (oldest first, from GET /api/conversations/:id) also puts every park and
// unpark into the history below.
export default function ConversationActivity({ conversationId, refreshKey, handleTimeMs, reopenedBy, reopenedAt, erpOrderIdOverride, erpOrderIdOverrideSetBy, erpOrderIdOverrideSetAt, parks = null, parkedTimeMs = null, newEmail = null, autoClosed = null, repairReopened = null, relatedReopened = null, orderLookupAttempts = null, taskOrderLookups = null, tasksCreated = null, vinEdits = null, isAdmin = false, highlightFeedbackId = null }) {
  const [suggestions, setSuggestions] = useState(null)
  // What each processed step submitted, by suggestion (2026-10-29), and the
  // Send Feedback reports on this contact (admin-only route).
  const [executions, setExecutions] = useState([])
  const [reports, setReports] = useState([])
  const [screenFeedbackId, setScreenFeedbackId] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  // Collapsed entries, by suggestion id. Every entry starts expanded. A new
  // conversation starts fresh; a reload of the same one (refreshKey) keeps
  // what the user collapsed, and any new entry arrives expanded.
  const [collapsed, setCollapsed] = useState(() => new Set())
  useEffect(() => { setCollapsed(new Set()) }, [conversationId])
  const returnLabels = useReturnLabels(conversationId, refreshKey ?? 0)
  const toggleEntry = (id) => setCollapsed((prev) => {
    const next = new Set(prev)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    return next
  })
  const entryIds = suggestions?.map((s) => s.id) ?? []
  // "Expand All" only once every entry is collapsed; otherwise "Collapse All".
  const allCollapsed = entryIds.length > 0 && entryIds.every((id) => collapsed.has(id))
  const toggleAll = () => setCollapsed(allCollapsed ? new Set() : new Set(entryIds))

  useEffect(() => {
    let active = true
    setLoading(true); setError(null)
    fetchSuggestions(conversationId)
      .then((list) => { if (active) setSuggestions(list) })
      .catch((e) => active && setError(e.message))
      .finally(() => active && setLoading(false))
    // Never fatal: the log still shows without them.
    fetchStepExecutions(conversationId)
      .then((list) => { if (active) setExecutions(Array.isArray(list) ? list : []) })
      .catch(() => { if (active) setExecutions([]) })
    if (isAdmin) {
      fetchFeedbackForConversation(conversationId)
        .then((list) => { if (active) setReports(Array.isArray(list) ? list : []) })
        .catch(() => { if (active) setReports([]) })
    } else {
      setReports([])
    }
    return () => { active = false }
  }, [conversationId, refreshKey, isAdmin])

  return (
    <Box sx={{ p: 3, overflowY: 'auto' }}>
      {/* Return steps waiting on the OMS API (2026-10-29): each step's
          checklist, as on the panel. */}
      {(returnLabels.data?.steps ?? []).length > 0 && (
        <Box sx={{ mb: 1.5 }} data-testid="activity-pending-labels">
          <Typography variant="subtitle2" color="text.secondary">Return steps waiting on the OMS API</Typography>
          {returnLabels.data.steps.map((row) => <PendingStepChecklist key={row.id} row={row} onCheckNow={returnLabels.checkNow} compact />)}
        </Box>
      )}
      <Box sx={{ mb: 1.5 }}>
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" flexWrap="wrap" useFlexGap>
          <Typography variant="subtitle2" color="text.secondary">
            Activity log — all AI actions and human decisions on this contact, newest first.
          </Typography>
          {entryIds.length > 0 && (
            <Button size="small" variant="outlined" onClick={toggleAll}>
              {allCollapsed ? 'Expand All' : 'Collapse All'}
            </Button>
          )}
        </Stack>
        {typeof handleTimeMs === 'number' && (
          <Typography variant="caption" color="text.secondary" display="block">
            Handle time: {formatHandleTime(handleTimeMs)}
          </Typography>
        )}
        {typeof parkedTimeMs === 'number' && parkedTimeMs > 0 && (
          <Typography variant="caption" color="text.secondary" display="block">
            Parked time: {formatHandleTime(parkedTimeMs)}
          </Typography>
        )}
        {reopenedAt && (
          <Typography variant="caption" color="text.secondary" display="block">
            Reopened by {reopenedBy ?? 'someone'} on {fmt(reopenedAt)}
          </Typography>
        )}
        {/* Recorded on the conversation, not a suggestion (2026-10-09): the
            pipeline's automatic close when our last message went to the
            customer, and the owner-run repair's reopen. The latest of each. */}
        {autoClosed?.at && (
          <Typography variant="caption" color="text.secondary" display="block" data-testid="activity-auto-closed">
            {autoClosed.reason} ({fmt(autoClosed.at)})
          </Typography>
        )}
        {/* Related contacts (2026-10-20): reopened because the contact that
            covered it was closed with no reply sent. */}
        {relatedReopened?.at && (
          <Typography variant="caption" color="text.secondary" display="block" data-testid="activity-related-reopened">
            {relatedReopened.reason} ({fmt(relatedReopened.at)})
          </Typography>
        )}
        {repairReopened?.at && (
          <Typography variant="caption" color="text.secondary" display="block" data-testid="activity-repair-reopened">
            {repairReopened.reason} ({fmt(repairReopened.at)})
          </Typography>
        )}
        {/* Any csr may set this (routes/ai.js's POST .../erp-order-override —
            no admin/senior gate), unlike handleTimeMs/reopenedAt above — but
            this whole component is still only ever rendered inside the
            admin/senior-gated Activity Log tab (see this file's own header
            comment), so today a plain csr who corrected the order still
            can't see this caption confirming it. Flagged back rather than
            silently relocated; a wider-visibility placement is a separate,
            deliberate call. */}
        {erpOrderIdOverrideSetAt && (
          <Typography variant="caption" color="text.secondary" display="block">
            Order manually corrected to {erpOrderIdOverride} by {erpOrderIdOverrideSetBy ?? 'someone'} on {fmt(erpOrderIdOverrideSetAt)}
          </Typography>
        )}
      </Box>
      {/* A "New" email (2026-10-08): the contact began with an agent's email,
          not an AI suggestion, so its send and the calls it made (Graph,
          and the TRQ order lookup) are listed here. */}
      {newEmail?.sentAt && (
        <Box sx={{ mb: 2 }} data-testid="activity-new-email">
          <Typography variant="caption" color="text.secondary" display="block">
            New email sent by {newEmail.sentBy ?? 'someone'} on {fmt(newEmail.sentAt)}
          </Typography>
          <ApiCallsSection apiCalls={newEmail.apiCalls} />
        </Box>
      )}
      {/* VIN edits from the header (2026-10-21; metadata.vinEdits, POST
          .../vin-override): "VIN changed from <previous or none> to <new> by
          <name>", newest first, each with the decode's calls. */}
      {[...(vinEdits ?? [])].reverse().map((e, i) => (
        <Box key={`vin-edit-${i}`} sx={{ mb: 2 }} data-testid="activity-vin-edit">
          <Typography variant="caption" color="text.secondary" display="block">
            {vinEditActivityLine(e)} ({fmt(e.at)})
          </Typography>
          {e.apiCalls?.length > 0 && <ApiCallsSection apiCalls={e.apiCalls} />}
        </Box>
      ))}
      {/* Tasks created from this contact (2026-10-15): "Task T1001 created by
          <agent>", newest first, each linking to the task. */}
      {[...(tasksCreated ?? [])].reverse().map((t) => (
        <Typography key={`task-created-${t.taskId}`} variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }} data-testid="activity-task-created">
          <Link href={`${TASKS_PATH}?task=${encodeURIComponent(t.taskId)}`} data-testid="activity-task-created-link">Task {t.displayId ?? `T${t.taskId}`}</Link> created by {t.createdBy ?? 'someone'} ({fmt(t.at)})
        </Typography>
      ))}
      {/* A task contact (2026-10-13): the order lookup its delivery, or an
          Order Details edit, made before any suggestion existed (the TRQ
          master search and the order read), newest first. */}
      {[...(taskOrderLookups ?? [])].reverse().map((lookup, i) => (
        <Box key={`task-lookup-${i}`} sx={{ mb: 2 }} data-testid="activity-task-order-lookup">
          <Typography variant="caption" color="text.secondary" display="block">
            {lookup.trigger === 'orderDetailsEdited' ? 'Order looked up again after the Order Number was edited' : 'Order looked up when the task was delivered'} ({fmt(lookup.at)})
          </Typography>
          <ApiCallsSection apiCalls={lookup.apiCalls} />
        </Box>
      ))}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading ? (
        <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>
      ) : !suggestions?.length && !parks?.length && !orderLookupAttempts?.length && !reports.length ? (
        <Typography color="text.secondary" variant="body2">No activity yet — no AI suggestions have been generated for this contact.</Typography>
      ) : (
        <Stack spacing={2}>
          {[
            ...(suggestions ?? []).map((s) => ({ kind: 'suggestion', at: s.createdAt, s })),
            ...parkActivityEntries(parks),
            ...(orderLookupAttempts ?? []).map((a, i) => ({ kind: 'lookupAttempt', at: a.at, a, key: `lookup-${i}-${a.at}` })),
            ...reports.map((r) => ({ kind: 'feedback', at: r.createdAt, r })),
          ]
            .sort((a, b) => new Date(b.at) - new Date(a.at))
            .map((e) => (e.kind === 'suggestion'
              ? <SuggestionCard key={e.s.id} s={e.s} expanded={!collapsed.has(e.s.id)} onToggle={toggleEntry} executions={executions.filter((x) => x.suggestionId === e.s.id)} />
              : e.kind === 'lookupAttempt'
                ? <OrderLookupAttemptEntry key={e.key} attempt={e.a} />
                : e.kind === 'feedback'
                  ? <FeedbackEntry key={`feedback-${e.r.id}`} report={e.r} highlighted={e.r.id === highlightFeedbackId} isAdmin={isAdmin} onViewScreen={setScreenFeedbackId} />
                  : <ParkEntry key={`${e.kind}-${e.park.id}`} entry={e} />))}
        </Stack>
      )}
      {screenFeedbackId && <FeedbackScreenView feedbackId={screenFeedbackId} onClose={() => setScreenFeedbackId(null)} />}
    </Box>
  )
}
